import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { SelectPanelWidthDirective } from "../../../../_helpers/select-panel-width.directive";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DateFilterTypeEnum } from "src/app/enums/dateFilterType.enum";
import { DateFormatService } from "src/app/services/dateFormat.service";
import {
    SampleStatusFilterValues,
    SAMPLE_STATUS_FILTER_DEFAULTS,
    STATUS_OPTIONS,
    DATE_RANGE_OPTIONS,
} from "./sample-status-widget.constants";
import { CustomDateRangeController } from "../_shared/custom-date-range-controller";
import { buildDateRangeOptionsWithRange } from "../_shared/date-filter.util";

@Component({
    standalone: true,
    selector: "app-sample-status-filters",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    FormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    SelectPanelWidthDirective
],
    templateUrl: "./sample-status-filters.component.html",
    styleUrls: ["./sample-status-filters.component.scss"]
})
export class SampleStatusFiltersComponent implements OnInit, OnDestroy {
    @Input() values: SampleStatusFilterValues = {
        ...SAMPLE_STATUS_FILTER_DEFAULTS,
    };
    @Input() compact = false;

    @Output() filterChange = new EventEmitter<SampleStatusFilterValues>();

    readonly statusOptions = STATUS_OPTIONS;
    dateRangeOptionsWithRange: { value: number; displayName: string }[] = [];
    readonly DateFilterTypeEnum = DateFilterTypeEnum;

    private destroy$ = new Subject<void>();
    private dateRangeCtrl: CustomDateRangeController;

    get customStartDateObj(): Date | undefined { return this.dateRangeCtrl.startDateObj; }
    get customEndDateObj(): Date | undefined { return this.dateRangeCtrl.endDateObj; }

    get customDateLabel(): string {
        return this.dateRangeCtrl.formatLabel(this.values.customStartDate, this.values.customEndDate);
    }

    constructor(
        dialog: MatDialog,
        private dateFormatService: DateFormatService,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {
        this.dateRangeCtrl = new CustomDateRangeController(dialog, dateFormatService, translate);
    }

    ngOnInit(): void {
        this.buildDateRangeOptions();
        this.dateRangeCtrl.syncFromStrings(this.values.customStartDate, this.values.customEndDate);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onStatusesChange(value: number[]): void {
        this.values = { ...this.values, selectedStatuses: value };
        this.filterChange.emit(this.values);
    }

    onDateFilterChange(value: number | undefined): void {
        if (this.values.dateFilterType === value) return;
        const prev = this.values.dateFilterType;
        this.values = { ...this.values, dateFilterType: value };

        if (value === DateFilterTypeEnum.Custom) {
            this.openCustomDateDialog(prev);
            return;
        }

        this.values = { ...this.values, customStartDate: null, customEndDate: null };
        this.dateRangeCtrl.syncFromStrings(null, null);
        this.filterChange.emit(this.values);
    }

    openCustomDateDialog(previousDateFilterType: number | undefined): void {
        this.dateRangeCtrl.openDialog().pipe(takeUntil(this.destroy$)).subscribe({
            next: result => {
                if (!result) {
                    this.values = { ...this.values, dateFilterType: previousDateFilterType };
                    this.filterChange.emit(this.values);
                    this.cdr.markForCheck();
                    return;
                }
                const { startIso, endIso } = this.dateRangeCtrl.applyResult(result);
                this.values = {
                    ...this.values,
                    dateFilterType: DateFilterTypeEnum.Custom,
                    customStartDate: startIso,
                    customEndDate: endIso,
                };
                this.filterChange.emit(this.values);
                this.cdr.markForCheck();
            },
        });
    }

    private buildDateRangeOptions(): void {
        this.dateRangeOptionsWithRange = buildDateRangeOptionsWithRange(
            DATE_RANGE_OPTIONS,
            this.translate,
            this.dateFormatService,
        );
    }

    public trackByIndex = trackByIndex;
}
